/**
 * lessonRenderer.js
 * Parses lesson markdown body into structured sections.
 * Uses clean :::type fenced callout format.
 */

// Strip all emoji unicode characters
export function stripEmoji(str) {
  if (!str) return str
  return str.replace(
    /[\u{1F300}-\u{1F9FF}]|[\u{1F000}-\u{1F02F}]|[\u{1F0A0}-\u{1F0FF}]|[\u{1F100}-\u{1F1FF}]|[\u{1F200}-\u{1F2FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{FE00}-\u{FE0F}]|\u{200D}|\u{FE0F}/gu, ''
  ).trim()
}

export function calloutLabel(type) {
  switch (type) {
    case 'tip':      return 'Things worth knowing'
    case 'warning':  return 'Watch out'
    case 'mistakes': return 'Watch out for these'
    case 'note':     return 'Note'
    default:         return 'Note'
  }
}

/**
 * Parse scenario blockquote content into structured chat data.
 * Handles: **User:** **You:** **User (Name):** **Name:** **Sarah:** etc.
 */
export function parseScenarioLines(lines) {
  const situation = []
  const chat = []
  const outcome = []
  let phase = 'situation'

  for (const raw of lines) {
    // Strip blockquote prefix if present
    const line = stripEmoji(raw.replace(/^>\s?/, '')).trim()
    if (!line) continue

    // Detect "What this shows" outcome marker
    const whatMatch = line.match(/^\*\*What this shows:?\*\*\s*(.*)/)
    if (whatMatch) {
      phase = 'outcome'
      const text = whatMatch[1].replace(/^"|"$/g, '').trim()
      if (text) outcome.push(text)
      continue
    }

    // Continue collecting outcome lines
    if (phase === 'outcome') {
      outcome.push(line.replace(/^\*\*|\*\*$/g, '').trim())
      continue
    }

    // Detect YOU lines — **You:** or **You (context):**
    const youMatch = line.match(/^\*\*You(?:\s*[^*]*)?\*\*:?\s*"?(.+)"?$/)
    if (youMatch) {
      phase = 'chat'
      chat.push({ speaker: 'you', text: youMatch[1].replace(/^"|"$/g, '').trim() })
      continue
    }

    // Section header detection — must happen before speaker matching
    // Handles **The situation:** and **The conversation:** with no trailing text
    const headerMatch = line.match(/^\*\*([^*]+)\*\*:?\s*$/)
    if (headerMatch) {
      const header = headerMatch[1].trim().toLowerCase()
      if (header.includes('situation') || header.includes('context') || header.includes('setup')) {
        phase = 'situation'
      } else if (header.includes('conversation') || header.includes('approach') ||
                 header.includes('investigation') || header.includes('what you do') ||
                 header.includes('your response')) {
        phase = 'chat'
      }
      // All section headers are suppressed — don't push to any array
      continue
    }

    // Detect USER lines — **User:** **User (Name):** **Name:** **Sarah:** etc.
    // Any **bold:** pattern that isn't "You" and isn't a section header
    const speakerMatch = line.match(/^\*\*([^*]+)\*\*:?\s*"?(.+)"?$/)
    if (speakerMatch) {
      const speaker = speakerMatch[1].trim().toLowerCase()
      const text = speakerMatch[2].replace(/^"|"$/g, '').trim()

      if (!text || text.length < 2) {
        continue // Skip malformed lines
      }

      phase = 'chat'
      // "you" speaker = YOU badge, everything else = USER badge
      const isYou = speaker === 'you' || speaker.startsWith('you ')
      chat.push({ speaker: isYou ? 'you' : 'user', text })
      continue
    }

    // Plain line — goes to current phase
    if (phase === 'situation') situation.push(line)
    else if (phase === 'outcome') outcome.push(line)
    // chat lines without bold formatting — skip (continuation lines)
  }

  return {
    situation: situation.join(' ').trim(),
    chat,
    outcome: outcome.join(' ').trim(),
  }
}

